import { AssetIcon } from '@/components/AssetIcon';
import { AssetBonusDisplay } from '@/components/AssetBonusDisplay';
import { GuideMenu } from '@/components/GuideMenu';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  getGuideBonusesData,
  type BonusDefinition,
  type TraitBonus,
} from '@/lib/bonuses-client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

// Content sections components for better organization
const IntroSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Personalities - Shaping Your Rule
    </h3>
    <div>
      <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
        The Power of Choice
      </h4>
      <p className="mb-4">
        Your personality in Mythea is more than just a title—it is a fundamental
        force that shapes how you rule. Each personality type brings unique
        strengths and strategies to your province, influencing everything from
        resource production to military might.
      </p>
      <p>
        Whether you choose to be a master of war, a cunning economist, or a
        powerful mage, your personality will define your approach to power and
        the tools at your disposal.
      </p>
    </div>
  </div>
);

const StrategiesSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Strategic Considerations
    </h3>
    <p>
      When selecting your personality, consider how it will complement your
      chosen race and align with your intended playstyle. Some combinations
      create powerful synergies, while others offer balanced versatility.
    </p>
    <ul className="list-disc list-inside space-y-2 pl-4">
      <li>
        Consider how your personality&apos;s strengths can enhance your racial
        bonuses or compensate for their weaknesses.
      </li>
      <li>
        Think about your preferred style of play—aggressive expansion, defensive
        building, or economic growth.
      </li>
      <li>
        Remember that some personalities excel in specific phases of the game,
        while others maintain consistent strength throughout.
      </li>
    </ul>
  </div>
);

const KingdomSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Kingdom Dynamics</h3>
    <p>
      Your personality choice affects not only your province but also your role
      within your kingdom. Different personalities can create powerful
      combinations when working together:
    </p>
    <ul className="list-disc list-inside space-y-2 pl-4">
      <li>
        Military personalities can lead the kingdom&apos;s armies and protect
        its borders.
      </li>
      <li>
        Economic personalities can boost the kingdom&apos;s resource production
        and trade capabilities.
      </li>
      <li>
        Magical personalities can provide crucial support and defensive
        capabilities.
      </li>
    </ul>
    <p className="italic">
      A well-balanced kingdom needs diverse personalities to thrive in all
      aspects of the game.
    </p>
  </div>
);

function PersonalityReferenceCard({
  personality,
  bonusDefinitionsById,
}: {
  personality: TraitBonus;
  bonusDefinitionsById: Map<number, BonusDefinition>;
}) {
  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <AssetIcon
          assetCode={personality.name}
          assetType="personality"
          size="small"
          onError={() => {
            // AssetIcon falls back to the default image.
          }}
        />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <h3 className="font-cinzel text-lg text-[#f0f0f0]">
              {personality.name}
            </h3>
            <p className="mt-1 text-sm text-[#b8b8b8]">
              {personality.description}
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#d4c8b2]">
                Province Bonuses
              </p>
              <AssetBonusDisplay
                asset={personality}
                bonusDefinitionsById={bonusDefinitionsById}
              />
            </div>
            {personality.kingdomInfluenceBonusDetails.length ? (
              <div className="rounded-lg border border-[rgba(212,175,55,0.16)] bg-[#0a0a0a]/70 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#f0d060]">
                  Kingdom Influence
                </p>
                <AssetBonusDisplay
                  asset={{
                    bonuses: personality.kingdomInfluenceBonuses,
                    bonusDetails: personality.kingdomInfluenceBonusDetails,
                  }}
                  bonusDefinitionsById={bonusDefinitionsById}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  );
}

export const Route = createFileRoute('/personalities/')({
  component: PersonalitiesPage,
});

function PersonalitiesPage() {
  const bonusesQuery = useQuery({
    queryKey: ['guide-bonuses', 'personalities'],
    queryFn: getGuideBonusesData,
    staleTime: 1000 * 60 * 10,
  });

  const personalities = bonusesQuery.data?.personalities || [];
  const bonusDefinitionsById = new Map(
    (bonusesQuery.data?.bonusDefinitions || []).map(definition => [
      definition.id,
      definition,
    ]),
  );

  return (
    <div className="min-h-screen bg-black text-white p-2 sm:p-4">
      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-8">
        <GuideMenu currentPath="/personalities" />

        {/* Main content section */}
        <section className="bg-white/[0.04] p-4 sm:p-6 rounded-lg border border-[rgba(212,175,55,0.12)]">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6 sm:mb-8">
            <img
              src="/images/assets/personality/warrior@2x.png"
              alt="Introduction"
              width={60}
              height={60}
              className="shrink-0 sm:w-20 sm:h-20"
            />
            <h2 className="text-xl sm:text-2xl font-bold">Personalities</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-8 sm:space-y-12 text-[#b8b8b8] leading-relaxed text-sm sm:text-base">
            <IntroSection />
            <StrategiesSection />
            <KingdomSection />
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>Current age</Badge>
            {bonusesQuery.data ? <Badge>{bonusesQuery.data.ageName}</Badge> : null}
            {bonusesQuery.data ? <Badge>Source: {bonusesQuery.data.source}</Badge> : null}
            {bonusesQuery.data ? (
              <Badge>{personalities.length} personalities</Badge>
            ) : null}
          </div>

          {bonusesQuery.isLoading ? (
            <Card className="p-4 text-sm text-[#b8b8b8]">
              Loading current age personalities...
            </Card>
          ) : null}

          {bonusesQuery.isError ? (
            <Card className="p-4 text-sm text-[#b8b8b8]">
              Current age personality data could not be loaded. The guide app
              needs the published bonuses and strings bucket manifests to render
              this reference.
            </Card>
          ) : null}

          {bonusesQuery.data && personalities.length ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {personalities.map(personality => (
                <PersonalityReferenceCard
                  key={personality.id}
                  personality={personality}
                  bonusDefinitionsById={bonusDefinitionsById}
                />
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
